// Helpers for decoding and persisting the cli_context value that the Altimate
// Code CLI appends to the URL fragment when opening the browser auth flow.

const SESSION_KEY = "altimate.cliContext";

export interface CliContext {
  v: number;
  machine_id: string;
  cli_version: string;
}

/** Decode cli_context from a base64url string, or return null on any error. */
export function decodeCliContext(raw: string | null): CliContext | null {
  if (!raw) return null;
  try {
    const decoded = JSON.parse(atob(raw.replace(/-/g, "+").replace(/_/g, "/")));
    if (typeof decoded !== "object" || decoded === null || Array.isArray(decoded)) return null;
    if (decoded.v !== 1) return null;
    if (typeof decoded.machine_id !== "string") return null;
    // cli_version is an untrusted field — a non-string (e.g. 123) would slip
    // through `?? ""` and register a non-string PostHog super-property.
    if (typeof decoded.cli_version !== "string") return null;
    return decoded as CliContext;
  } catch {
    return null;
  }
}

/** Stash cli_context in sessionStorage for survival across OAuth redirects. */
export function stashCliContext(ctx: CliContext): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(ctx));
}

/** Peek at stashed cli_context without removing it. */
export function peekCliContext(): CliContext | null {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CliContext;
  } catch {
    return null;
  }
}

/** Pop (read + remove) stashed cli_context. */
export function popCliContext(): CliContext | null {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(SESSION_KEY);
  try {
    return JSON.parse(raw) as CliContext;
  } catch {
    return null;
  }
}
